import { useState } from "react";
import { ArrowRight, CheckCircle, Send } from "lucide-react";
import { ACCENT, ACCENT_LIGHT } from "../constants/theme";
import Btn from "../components/Button";
import Card from "../components/Card";
import SectionLabel from "../components/SectionLabel";
import Input from "../components/Input";
import Select from "../components/Select";

export default function JoinUs() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    why: "",
    role: "",
  });

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const roles = [
    "Editor",
    "Script Writer",
    "Trainer",
    "Sound Designer",
    "Voice Over Artist",
  ];

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = "Full name is required";

    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Valid email required";

    if (!form.phone.trim()) e.phone = "Phone number is required";

    if (!form.why.trim()) e.why = "Please tell us why you want to join";

    if (!form.role) e.role = "Please select a role";

    return e;
  };

  const submit = (event) => {
    event.preventDefault();

    const e = validate();

    if (Object.keys(e).length) {
      setErrors(e);
      setSuccess(false);
      return;
    }

    const message = `
New Application Received:

Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Role: ${form.role}
Why Join: ${form.why}
    `;

    const whatsappNumber = "918320557290";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank", "noopener,noreferrer");

    setSuccess(true);
  };

  if (success) {
    return (
      <main
        aria-label="Application Success"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 40,
          background: "#fff",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 520 }}>
          <div
            aria-hidden="true"
            style={{
              width: 80,
              height: 80,
              background: ACCENT_LIGHT,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <CheckCircle size={40} color={ACCENT} />
          </div>

          <h1
            style={{
              fontWeight: 900,
              fontSize: 28,
              color: "#0f172a",
              marginBottom: 14,
            }}
          >
            Application Sent!
          </h1>

          <p style={{ color: "#64748b", fontSize: 16 }}>
            Your application has been sent via WhatsApp. Our team will contact you soon.
          </p>

          <Btn
            type="button"
            onClick={() => {
              setSuccess(false);
              setForm({
                name: "",
                phone: "",
                email: "",
                why: "",
                role: "",
              });
              setErrors({});
            }}
            style={{ marginTop: 28 }}
          >
            Submit Another <ArrowRight size={16} aria-hidden="true" />
          </Btn>
        </div>
      </main>
    );
  }

  return (
    <div>
      <section
        style={{
          background: ACCENT_LIGHT,
          padding: "72px 24px 56px",
          textAlign: "center",
        }}
      >
        <SectionLabel>Join Our Team</SectionLabel>

        <h1
          style={{
            fontSize: "clamp(30px, 5vw, 52px)",
            fontWeight: 900,
            color: "#0f172a",
            marginBottom: 14,
          }}
        >
          Create With Us
        </h1>

        <p style={{ color: "#475569", fontSize: 16 }}>
          Bring your skill, your story, and your perspective.
        </p>
      </section>

      <section style={{ background: "#fff", padding: "72px 24px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <Card style={{ padding: 40 }}>
            <form onSubmit={submit} noValidate>
              <h2
                style={{
                  fontWeight: 800,
                  fontSize: 22,
                  marginBottom: 28,
                }}
              >
                Your Application
              </h2>

              {[
                {
                  key: "name",
                  label: "Full Name",
                  placeholder: "Your full name",
                  type: "text",
                },
                {
                  key: "email",
                  label: "Email Address",
                  placeholder: "you@example.com",
                  type: "email",
                },
                {
                  key: "phone",
                  label: "Phone Number",
                  placeholder: "+91 98765 43210",
                  type: "tel",
                },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key}>
                  <Input
                    label={label}
                    type={type}
                    value={form[key]}
                    placeholder={placeholder}
                    required
                    onChange={(e) => {
                      setForm((p) => ({ ...p, [key]: e.target.value }));
                      setErrors((p) => ({ ...p, [key]: "" }));
                    }}
                  />

                  {errors[key] && (
                    <p role="alert" style={{ color: "#ef4444", fontSize: 12, marginTop: -10 }}>
                      {errors[key]}
                    </p>
                  )}
                </div>
              ))}

              <div>
                <Select
                  label="Role You're Applying For"
                  value={form.role}
                  options={roles}
                  required
                  onChange={(e) => {
                    setForm((p) => ({ ...p, role: e.target.value }));
                    setErrors((p) => ({ ...p, role: "" }));
                  }}
                />

                {errors.role && (
                  <p role="alert" style={{ color: "#ef4444", fontSize: 12, marginTop: -10 }}>
                    {errors.role}
                  </p>
                )}
              </div>

              <div>
                <Input
                  label="Why do you want to join?"
                  as="textarea"
                  rows={5}
                  value={form.why}
                  placeholder="Tell us about yourself..."
                  required
                  onChange={(e) => {
                    setForm((p) => ({ ...p, why: e.target.value }));
                    setErrors((p) => ({ ...p, why: "" }));
                  }}
                />

                {errors.why && (
                  <p role="alert" style={{ color: "#ef4444", fontSize: 12, marginTop: -10 }}>
                    {errors.why}
                  </p>
                )}
              </div>

              <Btn
                type="submit"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: 10,
                }}
              >
                Submit Application <Send size={16} aria-hidden="true" />
              </Btn>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
}